import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { cuitSchema, timezoneSchema, type CreateOrganizationRequest } from "@/contracts";
import { z } from "zod";

import { createOrganizationMutation } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";
import { ApiError } from "@/lib/api-error";

const DEFAULT_TIMEZONE = "America/Argentina/Cordoba";

const organizationFormFields = z.object({
  name: z.string().trim().min(2, "Enter a name of at least 2 characters.").max(120),
  // Optional, and only its shape is checked: the CUIT check digit is a rule
  // we have not validated against the AFIP specification, and rejecting a
  // real customer over a half-known rule is the worse failure.
  cuit: z
    .string()
    .trim()
    .refine((value) => value === "" || cuitSchema.safeParse(value).success, {
      message: "A CUIT has 11 digits, with or without dashes.",
    }),
  timezone: z.string().trim().pipe(timezoneSchema),
});

type OrganizationFormValues = z.infer<typeof organizationFormFields>;

export function OrganizationCreateDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const client = useApiClient();
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationFormFields),
    defaultValues: { name: "", cuit: "", timezone: DEFAULT_TIMEZONE },
  });

  const mutation = useMutation({
    ...createOrganizationMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });

  function close() {
    reset();
    mutation.reset();
    onClose();
  }

  const onSubmit = handleSubmit(async (values) => {
    const body: CreateOrganizationRequest = {
      name: values.name,
      timezone: values.timezone,
      ...(values.cuit ? { cuit: values.cuit } : {}),
    };

    try {
      await mutation.mutateAsync(body);
      close();
    } catch (error) {
      if (error instanceof ApiError) {
        for (const [field, message] of Object.entries(error.fieldErrors())) {
          if (field in organizationFormFields.shape) {
            setError(field as keyof OrganizationFormValues, { message });
          }
        }
      }
    }
  });

  return (
    <FormDialog
      open={open}
      title={t("dialogAddOrgTitle")}
      description={t("dialogAddOrgDescription")}
      submitLabel={t("createOrganization")}
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={close}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        <TextField
          label={tCommon("name")}
          required
          placeholder={t("placeholderOrgName")}
          {...(errors.name?.message === undefined ? {} : { error: errors.name.message })}
          {...register("name")}
        />

        <TextField
          label={tCommon("cuit")}
          hint={t("cuitHint")}
          placeholder="30-71234567-4"
          className="font-mono"
          {...(errors.cuit?.message === undefined ? {} : { error: errors.cuit.message })}
          {...register("cuit")}
        />

        <TextField
          label={t("fieldTimezone")}
          required
          hint={t("timezoneHint")}
          {...(errors.timezone?.message === undefined ? {} : { error: errors.timezone.message })}
          {...register("timezone")}
        />
      </div>
    </FormDialog>
  );
}
